import React, { HTMLAttributes, useEffect, useRef, useState } from "react";
import {
  useApplyVideoObjectFit,
  useAudioVideo,
  useRemoteVideoTileState,
} from "amazon-chime-sdk-component-library-react";
import { usePlatformStore } from "@platform/store";
import { VideoMeetingTile } from "./VideoMeetingTile";
import type { RoomMember } from "@core/features/videoMeeting";
import { useVideoFrameCapture } from "@platform/features/videoMeeting/hooks/useVideoFrameCapture";
import { useVideoHealthCheck } from "@platform/features/videoMeeting/hooks/useVideoHealthCheck";

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, "css"> {
  /** The tile ID to bind the video element to */
  tileId: number;
  /** The attendee ID for this tile */
  attendeeId: string;
  /** The name to show on the video's nameplate */
  name?: string;
  isWithContentShare?: boolean;
  roomMemberData?: RoomMember | null;
}

export const VideoMeetingTileRemoteVideoContainer: React.FC<
  React.PropsWithChildren<Props>
> = ({
  name,
  className,
  tileId,
  attendeeId,
  isWithContentShare,
  roomMemberData,
  ...rest
}) => {
  const audioVideo = useAudioVideo();
  const videoEl = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useRemoteVideoTileState();
  useApplyVideoObjectFit(videoEl);

  // Tile state from the unified observer — drives all visibility decisions
  const tileState = usePlatformStore(
    (s) => s.attendeeTileState[attendeeId] ?? 'avatar'
  );
  const visualEnabled = tileState === 'video' || tileState === 'frozen';
  const isBandwidthSuppressed = tileState === 'bandwidth_suppressed';

  // Canvas frame capture: capture while live, show frozen frame on simulcast drops
  const isCapturing = tileState === 'video';
  const showFrozenFrame = tileState === 'frozen';
  useVideoFrameCapture(videoEl, canvasRef, isCapturing);
  useVideoHealthCheck(videoEl, attendeeId, tileState);

  // Keep the last valid tile ID bound until the remote user explicitly turns
  // off their camera. Transient SDK tile drops (network blips, internal
  // rebinding) are ignored — the <video> element shows the last decoded frame.
  const [effectiveTileId, setEffectiveTileId] = useState(tileId);
  const lastValidTileIdRef = useRef(tileId > 0 ? tileId : 0);

  useEffect(() => {
    if (tileId > 0) {
      lastValidTileIdRef.current = tileId;
      setEffectiveTileId(tileId);
    }
  }, [tileId]);

  const hasValidTileId = effectiveTileId > 0;

  // Track previous tile ID so we can unbind old + bind new in the same
  // effect run, eliminating the gap between cleanup and re-bind.
  const prevTileIdRef = useRef<number>(0);

  useEffect(() => {
    if (!audioVideo || !videoEl.current || !hasValidTileId || !visualEnabled) {
      // Clear srcObject when not showing video — prevents green dead-track frames
      if (videoEl.current) {
        videoEl.current.srcObject = null;
      }
      return;
    }

    // Unbind previous tile if it changed (avoids gap frames)
    if (
      prevTileIdRef.current > 0 &&
      prevTileIdRef.current !== effectiveTileId
    ) {
      const prevTile = audioVideo.getVideoTile(prevTileIdRef.current);
      if (prevTile) {
        audioVideo.unbindVideoElement(prevTileIdRef.current);
      }
    }

    audioVideo.bindVideoElement(effectiveTileId, videoEl.current);
    prevTileIdRef.current = effectiveTileId;

    return () => {
      const tile = audioVideo.getVideoTile(effectiveTileId);
      if (tile) {
        audioVideo.unbindVideoElement(effectiveTileId);
      }
      prevTileIdRef.current = 0;
    };
  }, [audioVideo, effectiveTileId, hasValidTileId, visualEnabled]);

  return (
    <>
      <VideoMeetingTile
        {...rest}
        ref={videoEl}
        isWithContentShare={isWithContentShare}
        nameplate={name}
        className={className}
        isEnabled={visualEnabled && hasValidTileId}
        attendeeId={attendeeId}
        roomMemberData={roomMemberData}
        canvasRef={canvasRef}
        showFrozenFrame={showFrozenFrame}
        isBandwidthSuppressed={isBandwidthSuppressed}
      />
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute', top: 0, left: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.8)', color: '#0f0',
          fontSize: 10, padding: 4, pointerEvents: 'none',
          fontFamily: 'monospace', whiteSpace: 'pre',
        }}>
          {`state:${tileState} tile:${tileId} canvas:${showFrozenFrame ? 'show' : 'hidden'}`}
        </div>
      )}
    </>
  );
};

export default VideoMeetingTileRemoteVideoContainer;
